/**
 * People & Teams Hub
 * 
 * Premium workspace member management with visual role hierarchy,
 * team organization, invitation management, and role guide.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faUserShield,
  faUserPlus,
  faSearch,
  faCrown,
  faShieldAlt,
  faUserCog,
  faUserTie,
  faEye,
  faUserSecret,
  faEdit,
  faTrash,
  faClock,
  faCheck,
  faTimes,
  faBuilding,
  faLayerGroup,
  faLightbulb,
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faExchangeAlt,
  faSort,
  faArrowRight,
  faPaperPlane,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { 
  useRBAC, 
  useWorkspaces,
  useWorkspaceMembers, 
  useWorkspaceInvitations,
  useTeams,
  useSuggestions,
} from '../hooks/useRBAC';
import { PermissionGate, RoleBadge } from '../components/PermissionGate';
import { 
  WorkspaceRole, 
  WORKSPACE_ROLE_DISPLAY_NAMES, 
  WORKSPACE_ROLE_DESCRIPTIONS, 
  Permission 
} from '../types/rbac';
import useI18n from '../hooks/useI18n';
import '../styles/UserManagement.css';

const ROLE_ICONS: Record<WorkspaceRole, any> = {
  OWNER: faCrown,
  ORG_ADMIN: faShieldAlt,
  MANAGER: faUserCog,
  REVIEWER: faUserTie,
  VIEWER: faEye,
  GUEST: faUserSecret,
};

const ROLE_COLORS: Record<WorkspaceRole, string> = {
  OWNER: '#fbbf24',
  ORG_ADMIN: '#f472b6',
  MANAGER: '#667eea',
  REVIEWER: '#34d399',
  VIEWER: '#60a5fa',
  GUEST: '#a78bfa',
};

type SortMode = 'name' | 'role' | 'joined';

const UserManagement = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { workspaceRole, currentWorkspace, isPlatformAdmin, loading: rbacLoading } = useRBAC();
  const { convertToOrganization } = useWorkspaces();
  const { 
    members, 
    loading: membersLoading, 
    changeRole, 
    removeMember,
    transferOwnership 
  } = useWorkspaceMembers();
  const { 
    invitations, 
    loading: invitationsLoading, 
    inviteMember, 
    revokeInvitation 
  } = useWorkspaceInvitations();
  const { teams, loading: teamsLoading, createTeam } = useTeams();
  const { suggestions, loading: suggestionsLoading, reviewSuggestion } = useSuggestions();
  
  const [activeTab, setActiveTab] = useState<'members' | 'invitations' | 'teams' | 'roles' | 'suggestions'>('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('role');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>('VIEWER');
  const [, setInviteMessage] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [expandedRole, setExpandedRole] = useState<WorkspaceRole | null>(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);

  // Role hierarchy order for sorting
  const ROLE_ORDER: WorkspaceRole[] = ['OWNER', 'ORG_ADMIN', 'MANAGER', 'REVIEWER', 'VIEWER', 'GUEST'];

  // Stats
  const memberStats = useMemo(() => {
    const byRole: Record<string, number> = {};
    members.forEach(m => {
      byRole[m.role] = (byRole[m.role] || 0) + 1;
    });
    return { total: members.length, byRole, pendingInvites: invitations.length };
  }, [members, invitations]);

  // Filtered & sorted members
  const filteredMembers = useMemo(() => {
    let result = members.filter(m => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        m.user?.email?.toLowerCase().includes(q) ||
        m.user?.firstName?.toLowerCase().includes(q) ||
        m.user?.lastName?.toLowerCase().includes(q) ||
        m.role?.toLowerCase().includes(q)
      );
    });
    
    result.sort((a, b) => {
      switch (sortMode) {
        case 'name':
          return (a.user?.firstName || '').localeCompare(b.user?.firstName || '');
        case 'role':
          return ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
        case 'joined':
          return new Date(b.joinedAt).getTime() - new Date(a.joinedAt).getTime();
        default:
          return 0;
      }
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, searchQuery, sortMode]);

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setActionFeedback({ type, message });
    setTimeout(() => setActionFeedback(null), 3000);
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    if (currentWorkspace?.type === 'PERSONAL') {
      showFeedback('error', t('userMgmt.error.personalNoInvitations' as any));
      setShowInviteModal(false);
      setShowConvertModal(true);
      return;
    }
    try {
      await inviteMember(inviteEmail, inviteRole);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('VIEWER');
      setInviteMessage('');
      showFeedback('success', t('userMgmt.success.invitationSent' as any).replace('{email}', inviteEmail));
    } catch (error: any) {
      showFeedback('error', error.response?.data?.error || error.message || t('userMgmt.error.invitationFailed' as any));
    }
  };

  const handleConvertToOrg = async () => {
    if (!currentWorkspace) return;
    try {
      setConverting(true);
      await convertToOrganization(currentWorkspace.id);
      setShowConvertModal(false);
      showFeedback('success', t('userMgmt.success.convertedToOrg' as any));
    } catch (error: any) {
      showFeedback('error', error.response?.data?.error || error.message || t('userMgmt.error.convertFailed' as any));
    } finally {
      setConverting(false);
    }
  };

  const handleRoleChange = async (newRole: WorkspaceRole) => {
    if (!selectedMember) return;
    try {
      await changeRole(selectedMember.userId, newRole);
      setShowRoleModal(false);
      setSelectedMember(null);
      showFeedback('success', t('userMgmt.success.roleUpdated' as any));
    } catch (error: any) {
      showFeedback('error', error.message || t('userMgmt.error.roleChangeFailed' as any));
    }
  };

  const handleRemoveMember = async (userId: string, name: string) => {
    if (!window.confirm(t('userMgmt.confirm.removeMember' as any).replace('{name}', name))) return;
    try {
      await removeMember(userId);
      showFeedback('success', t('userMgmt.success.memberRemoved' as any).replace('{name}', name));
    } catch (error: any) {
      showFeedback('error', error.message || t('userMgmt.error.removeMemberFailed' as any));
    }
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    if (!window.confirm(t('userMgmt.confirm.revokeInvitation' as any).replace('{email}', email))) return;
    try {
      await revokeInvitation(invitationId);
      showFeedback('success', t('userMgmt.success.invitationRevoked' as any).replace('{email}', email));
    } catch (error: any) {
      showFeedback('error', error.message || t('userMgmt.error.revokeInvitationFailed' as any));
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferTargetId) return;
    if (!window.confirm(t('userMgmt.confirm.transferOwnership' as any))) return;
    try {
      await transferOwnership(transferTargetId);
      setShowTransferModal(false);
      setTransferTargetId('');
      showFeedback('success', t('userMgmt.success.ownershipTransferred' as any));
    } catch (error: any) {
      showFeedback('error', error.message || t('userMgmt.error.transferOwnershipFailed' as any));
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName) return;
    try {
      await createTeam({ name: newTeamName, description: newTeamDescription });
      setShowTeamModal(false);
      setNewTeamName('');
      setNewTeamDescription('');
      showFeedback('success', t('userMgmt.success.teamCreated' as any).replace('{name}', newTeamName));
    } catch (error: any) {
      showFeedback('error', error.message || t('userMgmt.error.createTeamFailed' as any));
    }
  };

  const handleReviewSuggestion = async (suggestionId: string, approved: boolean) => {
    try {
      await reviewSuggestion(suggestionId, approved);
      showFeedback('success', approved ? t('userMgmt.success.suggestionApproved' as any) : t('userMgmt.success.suggestionRejected' as any));
    } catch (error: any) {
      showFeedback('error', error.message || t('userMgmt.error.reviewSuggestionFailed' as any));
    }
  };

  const getAssignableRoles = (): WorkspaceRole[] => {
    if (workspaceRole === 'OWNER' || isPlatformAdmin) return ['ORG_ADMIN', 'MANAGER', 'REVIEWER', 'VIEWER', 'GUEST'];
    if (workspaceRole === 'ORG_ADMIN') return ['MANAGER', 'REVIEWER', 'VIEWER', 'GUEST'];
    if (workspaceRole === 'MANAGER') return ['REVIEWER', 'VIEWER', 'GUEST'];
    return [];
  };

  const getRelativeTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return t('userMgmt.time.today' as any);
    if (days === 1) return t('userMgmt.time.yesterday' as any);
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  // While RBAC is still initialising, don't show "no workspace" — the workspace list
  // may not have been loaded yet (e.g. after a plan change that caused a permissions
  // fetch failure but the workspace data itself is still valid).
  if (rbacLoading) {
    return (
      <div className="um-page">
        <div className="um-empty-state">
          <div className="um-empty-icon" style={{ opacity: 0.5 }}><FontAwesomeIcon icon={faBuilding} /></div>
          <p style={{ color: 'var(--text-muted)' }}>{t('common.loading' as any) || 'Loading…'}</p>
        </div>
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="um-page">
        <div className="um-empty-state">
          <div className="um-empty-icon"><FontAwesomeIcon icon={faBuilding} /></div>
          <h3>{t('userMgmt.empty.noWorkspaceTitle' as any)}</h3>
          <p>{t('userMgmt.empty.noWorkspaceDesc' as any)}</p>
          <button className="um-btn-primary" onClick={() => navigate('/workspaces')}>
            <FontAwesomeIcon icon={faArrowRight} /> {t('userMgmt.empty.goToWorkspaces' as any)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="um-page">
      {/* Feedback Toast */}
      {actionFeedback && (
        <div className={`um-toast um-toast-${actionFeedback.type}`}>
          <FontAwesomeIcon icon={actionFeedback.type === 'success' ? faCheck : faTimes} />
          <span>{actionFeedback.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="um-header">
        <div className="um-header-content">
          <div className="um-header-badge">
            <FontAwesomeIcon icon={faUsers} /> {t('userMgmt.header.badge' as any)}
          </div>
          <h1>{currentWorkspace.name}</h1>
          <p>{t('userMgmt.header.description' as any)}</p>
        </div>
        <div className="um-header-actions">
          {currentWorkspace?.type === 'ORGANIZATION' ? (
            <PermissionGate permission={Permission.MEMBER_INVITE}>
              <button className="um-btn-primary" onClick={() => setShowInviteModal(true)}>
                <FontAwesomeIcon icon={faUserPlus} /> {t('userMgmt.header.invitePeople' as any)}
              </button>
            </PermissionGate>
          ) : (
            <button className="um-btn-secondary" onClick={() => setShowConvertModal(true)}>
              <FontAwesomeIcon icon={faBuilding} /> {t('userMgmt.header.convertToOrg' as any)}
            </button>
          )}
          {workspaceRole === 'OWNER' && currentWorkspace?.type === 'ORGANIZATION' && (
            <button className="um-btn-secondary" onClick={() => setShowTransferModal(true)}>
              <FontAwesomeIcon icon={faExchangeAlt} /> {t('userMgmt.header.transferOwnership' as any)}
            </button>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="um-stats-bar">
        <div className="um-stat">
          <span className="um-stat-value">{memberStats.total}</span>
          <span className="um-stat-label">{t('userMgmt.stats.members' as any)}</span>
        </div>
        <div className="um-stat-divider" />
        {ROLE_ORDER.filter(r => memberStats.byRole[r]).map(role => (
          <div key={role} className="um-stat">
            <span className="um-stat-value" style={{ color: ROLE_COLORS[role] }}>
              {memberStats.byRole[role] || 0}
            </span>
            <span className="um-stat-label">{WORKSPACE_ROLE_DISPLAY_NAMES[role]}s</span>
          </div>
        ))}
        {memberStats.pendingInvites > 0 && (
          <>
            <div className="um-stat-divider" />
            <div className="um-stat">
              <span className="um-stat-value" style={{ color: '#f97316' }}>{memberStats.pendingInvites}</span>
              <span className="um-stat-label">{t('userMgmt.stats.pending' as any)}</span>
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="um-tabs">
        {[
          { key: 'members', label: t('userMgmt.tabs.members' as any), icon: faUsers, count: members.length },
          { key: 'invitations', label: t('userMgmt.tabs.invitations' as any), icon: faEnvelope, count: invitations.length, permission: Permission.MEMBER_INVITE },
          { key: 'teams', label: t('userMgmt.tabs.teams' as any), icon: faLayerGroup, count: teams.length },
          { key: 'roles', label: t('userMgmt.tabs.roleGuide' as any), icon: faUserShield },
          { key: 'suggestions', label: t('userMgmt.tabs.suggestions' as any), icon: faLightbulb, count: suggestions.length, alert: true, permission: Permission.ITEM_APPROVE_SUGGESTIONS },
        ].map(tab => {
          const TabBtn = (
            <button
              key={tab.key}
              className={`um-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key as any)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              <span>{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`um-tab-badge ${tab.alert ? 'alert' : ''}`}>{tab.count}</span>
              )}
            </button>
          );
          if (tab.permission) {
            return <PermissionGate key={tab.key} permission={tab.permission}>{TabBtn}</PermissionGate>;
          }
          return TabBtn;
        })}
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div className="um-section">
          <div className="um-section-toolbar">
            <div className="um-search">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder={t('userMgmt.members.searchPlaceholder' as any)}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}><FontAwesomeIcon icon={faTimes} /></button>
              )}
            </div>
            <div className="um-sort">
              <FontAwesomeIcon icon={faSort} />
              <select value={sortMode} onChange={(e) => setSortMode(e.target.value as SortMode)}>
                <option value="role">{t('userMgmt.members.sortByRole' as any)}</option>
                <option value="name">{t('userMgmt.members.sortByName' as any)}</option>
                <option value="joined">{t('userMgmt.members.sortByJoined' as any)}</option>
              </select>
            </div>
          </div>

          {membersLoading ? (
            <div className="um-loading">{t('userMgmt.members.loading' as any)}</div>
          ) : filteredMembers.length === 0 ? (
            <div className="um-empty-state">
              <FontAwesomeIcon icon={faSearch} />
              <h4>{t('userMgmt.members.notFoundTitle' as any)}</h4>
              <p>{searchQuery ? `No results for "${searchQuery}"` : 'No members in this workspace'}</p>
            </div>
          ) : (
            <div className="um-members-list">
              {filteredMembers.map(member => {
                const memberName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || t('userMgmt.members.unknownName' as any);
                const roleColor = ROLE_COLORS[member.role as WorkspaceRole] || '#667eea';
                
                return (
                  <div key={member.id} className="um-member-row">
                    <div className="um-member-avatar" style={{ borderColor: roleColor }}>
                      {member.user?.profilePicture ? (
                        <img src={member.user.profilePicture} alt="" />
                      ) : (
                        <span>{memberName.charAt(0).toUpperCase()}</span>
                      )}
                      <div className="um-member-role-dot" style={{ background: roleColor }} title={WORKSPACE_ROLE_DISPLAY_NAMES[member.role as WorkspaceRole]} />
                    </div>
                    <div className="um-member-info">
                      <div className="um-member-name">
                        {memberName}
                        {member.role === 'OWNER' && <FontAwesomeIcon icon={faCrown} style={{ color: '#fbbf24', marginLeft: 6, fontSize: 12 }} />}
                      </div>
                      <div className="um-member-email">{member.user?.email}</div>
                    </div>
                    <div className="um-member-role">
                      <RoleBadge role={member.role} size="sm" />
                    </div>
                    <div className="um-member-joined">
                      <FontAwesomeIcon icon={faClock} />
                      {getRelativeTime(member.joinedAt)}
                    </div>
                    <div className="um-member-actions">
                      {member.role !== 'OWNER' && (
                        <>
                          <PermissionGate permission={Permission.MEMBER_EDIT_ROLES}>
                            <button 
                              className="um-action-btn"
                              onClick={() => { setSelectedMember(member); setShowRoleModal(true); }}
                              title={t('userMgmt.members.changeRoleTooltip' as any)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission={Permission.MEMBER_REMOVE}>
                            <button 
                              className="um-action-btn danger"
                              onClick={() => handleRemoveMember(member.userId, memberName)}
                              title={t('userMgmt.members.removeMemberTooltip' as any)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionGate>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="um-section">
          <div className="um-section-header">
            <h3>{t('userMgmt.invitations.title' as any)}</h3>
            <button className="um-btn-primary um-btn-sm" onClick={() => setShowInviteModal(true)}>
              <FontAwesomeIcon icon={faUserPlus} /> {t('userMgmt.invitations.sendInvite' as any)}
            </button>
          </div>
          {invitationsLoading ? (
            <div className="um-loading">{t('userMgmt.invitations.loading' as any)}</div>
          ) : invitations.length === 0 ? (
            <div className="um-empty-state">
              <div className="um-empty-icon"><FontAwesomeIcon icon={faEnvelope} /></div>
              <h4>{t('userMgmt.invitations.emptyTitle' as any)}</h4>
              <p>{t('userMgmt.invitations.emptyDesc' as any)}</p>
              <button className="um-btn-primary" onClick={() => setShowInviteModal(true)}>
                <FontAwesomeIcon icon={faUserPlus} /> {t('userMgmt.invitations.inviteSomeone' as any)}
              </button>
            </div>
          ) : (
            <div className="um-invitations-list">
              {invitations.map(inv => (
                <div key={inv.id} className="um-invitation-row">
                  <div className="um-invitation-avatar">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <div className="um-invitation-info">
                    <div className="um-invitation-email">{inv.email}</div>
                    <div className="um-invitation-meta">
                      <RoleBadge role={inv.role} size="sm" />
                      <span className="um-invitation-expires">
                        <FontAwesomeIcon icon={faClock} /> {t('userMgmt.invitations.expires' as any)} {new Date(inv.expiresAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="um-invitation-actions">
                    <button 
                      className="um-action-btn danger"
                      onClick={() => handleRevokeInvitation(inv.id, inv.email)}
                      title={t('userMgmt.invitations.revokeTooltip' as any)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Teams Tab */}
      {activeTab === 'teams' && (
        <div className="um-section">
          <div className="um-section-header">
            <h3>{t('userMgmt.teams.title' as any)}</h3>
            <PermissionGate permission={Permission.TEAM_CREATE}>
              <button className="um-btn-primary um-btn-sm" onClick={() => setShowTeamModal(true)}>
                <FontAwesomeIcon icon={faLayerGroup} /> {t('userMgmt.teams.newTeam' as any)}
              </button>
            </PermissionGate>
          </div>

          {teamsLoading ? (
            <div className="um-loading">{t('userMgmt.teams.loading' as any)}</div>
          ) : teams.length === 0 ? (
            <div className="um-empty-state">
              <div className="um-empty-icon"><FontAwesomeIcon icon={faLayerGroup} /></div>
              <h4>{t('userMgmt.teams.emptyTitle' as any)}</h4>
              <p>{t('userMgmt.teams.emptyDesc' as any)}</p>
              <PermissionGate permission={Permission.TEAM_CREATE}>
                <button className="um-btn-primary" onClick={() => setShowTeamModal(true)}>
                  <FontAwesomeIcon icon={faLayerGroup} /> {t('userMgmt.teams.createFirst' as any)}
                </button>
              </PermissionGate>
            </div>
          ) : (
            <div className="um-teams-grid">
              {teams.map(team => (
                <div key={team.id} className="um-team-card" onClick={() => navigate(`/teams/${team.id}`)}>
                  <div className="um-team-icon">
                    <FontAwesomeIcon icon={faLayerGroup} />
                  </div>
                  <div className="um-team-info">
                    <h4>{team.name}</h4>
                    <p>{team.description || t('userMgmt.teams.noDescription' as any)}</p>
                  </div>
                  <div className="um-team-stats">
                    <span><FontAwesomeIcon icon={faUsers} /> {team._count?.members || 0}</span>
                    <span><FontAwesomeIcon icon={faLayerGroup} /> {team._count?.items || 0}</span>
                  </div>
                  <FontAwesomeIcon icon={faArrowRight} className="um-team-arrow" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Roles Guide Tab */}
      {activeTab === 'roles' && (
        <div className="um-section">
          <div className="um-roles-header">
            <div className="um-roles-header-icon">
              <FontAwesomeIcon icon={faUserShield} />
            </div>
            <div>
              <h3>{t('userMgmt.roles.title' as any)}</h3>
              <p>{t('userMgmt.roles.description' as any)}</p>
            </div>
          </div>

          <div className="um-roles-list">
            {ROLE_ORDER.map((role, idx) => {
              const color = ROLE_COLORS[role];
              const memberCount = memberStats.byRole[role] || 0;
              
              return (
                <div 
                  key={role} 
                  className={`um-role-card ${expandedRole === role ? 'expanded' : ''}`}
                  style={{ '--role-color': color } as any}
                >
                  <div 
                    className="um-role-header"
                    onClick={() => setExpandedRole(expandedRole === role ? null : role)}
                  >
                    <div className="um-role-rank">{idx + 1}</div>
                    <div className="um-role-icon-wrap" style={{ background: `${color}15`, color }}>
                      <FontAwesomeIcon icon={ROLE_ICONS[role]} />
                    </div>
                    <div className="um-role-info">
                      <h4>{WORKSPACE_ROLE_DISPLAY_NAMES[role]}</h4>
                      <p>{WORKSPACE_ROLE_DESCRIPTIONS[role]}</p>
                    </div>
                    {memberCount > 0 && (
                      <span className="um-role-count" style={{ background: `${color}15`, color }}>
                        {memberCount} {memberCount === 1 ? t('userMgmt.roles.memberCount' as any) : t('userMgmt.roles.membersCount' as any)}
                      </span>
                    )}
                    <FontAwesomeIcon 
                      icon={expandedRole === role ? faChevronUp : faChevronDown} 
                      className="um-role-chevron"
                    />
                  </div>

                  {expandedRole === role && (
                    <div className="um-role-details">
                      <h5>{t('userMgmt.roles.permissionsHeading' as any)}</h5>
                      <div className="um-permissions-grid">
                        {role === 'OWNER' && [t('userMgmt.roles.owner.fullControl' as any), t('userMgmt.roles.owner.transferOwnership' as any), t('userMgmt.roles.owner.deleteWorkspace' as any), t('userMgmt.roles.owner.manageBilling' as any), t('userMgmt.roles.owner.allAdmin' as any)].map(p => (
                          <span key={p} className="um-perm granted"><FontAwesomeIcon icon={faCheck} /> {p}</span>
                        ))}
                        {role === 'ORG_ADMIN' && [t('userMgmt.roles.orgAdmin.manageSettings' as any), t('userMgmt.roles.orgAdmin.inviteRemove' as any), t('userMgmt.roles.orgAdmin.assignRoles' as any), t('userMgmt.roles.orgAdmin.viewAll' as any), t('userMgmt.roles.orgAdmin.configureAI' as any)].map(p => (
                          <span key={p} className="um-perm granted"><FontAwesomeIcon icon={faCheck} /> {p}</span>
                        ))}
                        {role === 'MANAGER' && (
                          <>
                            {[t('userMgmt.roles.manager.createEdit' as any), t('userMgmt.roles.manager.approveSuggestions' as any), t('userMgmt.roles.manager.moderateComments' as any), t('userMgmt.roles.manager.useAI' as any), t('userMgmt.roles.manager.createShareLinks' as any)].map(p => (
                              <span key={p} className="um-perm granted"><FontAwesomeIcon icon={faCheck} /> {p}</span>
                            ))}
                            {[t('userMgmt.roles.manager.cannotManageMembers' as any), t('userMgmt.roles.manager.cannotChangeSettings' as any)].map(p => (
                              <span key={p} className="um-perm denied"><FontAwesomeIcon icon={faTimes} /> {p}</span>
                            ))}
                          </>
                        )}
                        {role === 'REVIEWER' && (
                          <>
                            {[t('userMgmt.roles.reviewer.viewItems' as any), t('userMgmt.roles.reviewer.suggestEdits' as any), t('userMgmt.roles.reviewer.postComments' as any)].map(p => (
                              <span key={p} className="um-perm granted"><FontAwesomeIcon icon={faCheck} /> {p}</span>
                            ))}
                            {[t('userMgmt.roles.reviewer.cannotEdit' as any), t('userMgmt.roles.reviewer.cannotCreate' as any), t('userMgmt.roles.reviewer.cannotManage' as any)].map(p => (
                              <span key={p} className="um-perm denied"><FontAwesomeIcon icon={faTimes} /> {p}</span>
                            ))}
                          </>
                        )}
                        {role === 'VIEWER' && (
                          <>
                            {[t('userMgmt.roles.viewer.viewItems' as any), t('userMgmt.roles.viewer.downloadFiles' as any), t('userMgmt.roles.viewer.viewComments' as any)].map(p => (
                              <span key={p} className="um-perm granted"><FontAwesomeIcon icon={faCheck} /> {p}</span>
                            ))}
                            {[t('userMgmt.roles.viewer.cannotEdit' as any), t('userMgmt.roles.viewer.readOnly' as any), t('userMgmt.roles.viewer.cannotManage' as any)].map(p => (
                              <span key={p} className="um-perm denied"><FontAwesomeIcon icon={faTimes} /> {p}</span>
                            ))}
                          </>
                        )}
                        {role === 'GUEST' && (
                          <>
                            {[t('userMgmt.roles.guest.limitedView' as any), t('userMgmt.roles.guest.accessShared' as any)].map(p => (
                              <span key={p} className="um-perm granted"><FontAwesomeIcon icon={faCheck} /> {p}</span>
                            ))}
                            {[t('userMgmt.roles.guest.cannotSeeAll' as any), t('userMgmt.roles.guest.mostRestrictive' as any), t('userMgmt.roles.guest.noManagement' as any)].map(p => (
                              <span key={p} className="um-perm denied"><FontAwesomeIcon icon={faTimes} /> {p}</span>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Suggestions Tab */}
      {activeTab === 'suggestions' && (
        <div className="um-section">
          {suggestionsLoading ? (
            <div className="um-loading">{t('userMgmt.suggestions.loading' as any)}</div>
          ) : suggestions.length === 0 ? (
            <div className="um-empty-state">
              <div className="um-empty-icon"><FontAwesomeIcon icon={faLightbulb} /></div>
              <h4>{t('userMgmt.suggestions.emptyTitle' as any)}</h4>
              <p>{t('userMgmt.suggestions.emptyDesc' as any)}</p>
            </div>
          ) : (
            <div className="um-suggestions-list">
              {suggestions.map(suggestion => (
                <div key={suggestion.id} className="um-suggestion-card">
                  <div className="um-suggestion-header">
                    <span className="um-suggestion-field">{suggestion.fieldName}</span>
                  </div>
                  <div className="um-suggestion-values">
                    <div className="um-suggestion-old">
                      <span className="um-suggestion-label">{t('userMgmt.suggestions.currentLabel' as any)}</span>
                      <code>{suggestion.currentValue || t('userMgmt.suggestions.empty' as any)}</code>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} className="um-suggestion-arrow" />
                    <div className="um-suggestion-new">
                      <span className="um-suggestion-label">{t('userMgmt.suggestions.suggestedLabel' as any)}</span>
                      <code>{suggestion.suggestedValue}</code>
                    </div>
                  </div>
                  {suggestion.explanation && (
                    <p className="um-suggestion-explanation">
                      <FontAwesomeIcon icon={faInfoCircle} /> {suggestion.explanation}
                    </p>
                  )}
                  <div className="um-suggestion-actions">
                    <button 
                      className="um-approve-btn"
                      onClick={() => handleReviewSuggestion(suggestion.id, true)}
                    >
                      <FontAwesomeIcon icon={faCheck} /> {t('userMgmt.suggestions.approve' as any)}
                    </button>
                    <button 
                      className="um-reject-btn"
                      onClick={() => handleReviewSuggestion(suggestion.id, false)}
                    >
                      <FontAwesomeIcon icon={faTimes} /> {t('userMgmt.suggestions.reject' as any)}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><FontAwesomeIcon icon={faUserPlus} /> {t('userMgmt.inviteModal.title' as any)}</h2>
              <button className="close-btn" onClick={() => setShowInviteModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="um-modal-body">
              <div className="form-group">
                <label>{t('userMgmt.inviteModal.emailLabel' as any)}</label>
                <input
                  type="email"
                  placeholder={t('userMgmt.inviteModal.emailPlaceholder' as any)}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>{t('userMgmt.inviteModal.roleLabel' as any)}</label>
                <div className="um-role-picker">
                  {getAssignableRoles().map(role => (
                    <button
                      key={role}
                      className={`um-role-option ${inviteRole === role ? 'selected' : ''}`}
                      onClick={() => setInviteRole(role)}
                      style={{ '--role-color': ROLE_COLORS[role] } as any}
                    >
                      <FontAwesomeIcon icon={ROLE_ICONS[role]} />
                      <span>{WORKSPACE_ROLE_DISPLAY_NAMES[role]}</span>
                    </button>
                  ))}
                </div>
                <p className="um-role-desc">{WORKSPACE_ROLE_DESCRIPTIONS[inviteRole]}</p>
              </div>
            </div>
            <div className="um-modal-footer">
              <button className="btn-secondary" onClick={() => setShowInviteModal(false)}>{t('userMgmt.common.cancel' as any)}</button>
              <button className="um-btn-primary" onClick={handleInvite} disabled={!inviteEmail}>
                <FontAwesomeIcon icon={faPaperPlane} /> {t('userMgmt.inviteModal.sendInvitation' as any)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><FontAwesomeIcon icon={faEdit} /> {t('userMgmt.roleModal.title' as any)}</h2>
              <button className="close-btn" onClick={() => setShowRoleModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="um-modal-body">
              <p style={{ color: '#a0aec0', marginBottom: 16 }}>
                {t('userMgmt.roleModal.changingRoleFor' as any)} <strong style={{ color: '#fff' }}>{selectedMember.user?.firstName} {selectedMember.user?.lastName}</strong> ({selectedMember.user?.email})
              </p>
              <div className="um-role-picker vertical">
                {getAssignableRoles().map(role => (
                  <button
                    key={role}
                    className={`um-role-option-full ${selectedMember.role === role ? 'current' : ''}`}
                    onClick={() => handleRoleChange(role)}
                    style={{ '--role-color': ROLE_COLORS[role] } as any}
                  >
                    <div className="um-role-option-icon" style={{ background: `${ROLE_COLORS[role]}15`, color: ROLE_COLORS[role] }}>
                      <FontAwesomeIcon icon={ROLE_ICONS[role]} />
                    </div>
                    <div className="um-role-option-info">
                      <span className="um-role-option-name">{WORKSPACE_ROLE_DISPLAY_NAMES[role]}</span>
                      <span className="um-role-option-desc">{WORKSPACE_ROLE_DESCRIPTIONS[role]}</span>
                    </div>
                    {selectedMember.role === role && <span className="um-current-tag">{t('userMgmt.roleModal.currentTag' as any)}</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="um-modal-footer">
              <button className="btn-secondary" onClick={() => setShowRoleModal(false)}>{t('userMgmt.common.cancel' as any)}</button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="um-modal um-modal-danger" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><FontAwesomeIcon icon={faExchangeAlt} /> {t('userMgmt.transferModal.title' as any)}</h2>
              <button className="close-btn" onClick={() => setShowTransferModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="um-modal-body">
              <div className="um-transfer-warning">
                <FontAwesomeIcon icon={faExchangeAlt} />
                <div>
                  <strong>{t('userMgmt.transferModal.warningTitle' as any)}</strong>
                  <p>{t('userMgmt.transferModal.warningDesc' as any)}</p>
                </div>
              </div>
              <div className="form-group">
                <label>{t('userMgmt.transferModal.newOwnerLabel' as any)}</label>
                <select 
                  value={transferTargetId}
                  onChange={(e) => setTransferTargetId(e.target.value)}
                >
                  <option value="">{t('userMgmt.transferModal.selectMemberPlaceholder' as any)}</option>
                  {members
                    .filter(m => m.role !== 'OWNER')
                    .map(m => (
                      <option key={m.userId} value={m.userId}>
                        {m.user?.firstName} {m.user?.lastName} — {m.user?.email} ({WORKSPACE_ROLE_DISPLAY_NAMES[m.role as WorkspaceRole]})
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="um-modal-footer">
              <button className="btn-secondary" onClick={() => setShowTransferModal(false)}>{t('userMgmt.common.cancel' as any)}</button>
              <button className="um-btn-danger" onClick={handleTransferOwnership} disabled={!transferTargetId}>
                {t('userMgmt.transferModal.transferBtn' as any)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Team Modal */}
      {showTeamModal && (
        <div className="modal-overlay" onClick={() => setShowTeamModal(false)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><FontAwesomeIcon icon={faLayerGroup} /> {t('userMgmt.teamModal.title' as any)}</h2>
              <button className="close-btn" onClick={() => setShowTeamModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="um-modal-body">
              <div className="form-group">
                <label>{t('userMgmt.teamModal.nameLabel' as any)}</label>
                <input
                  type="text"
                  placeholder={t('userMgmt.teamModal.namePlaceholder' as any)}
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>{t('userMgmt.teamModal.descLabel' as any)}</label>
                <textarea
                  placeholder={t('userMgmt.teamModal.descPlaceholder' as any)}
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <div className="um-modal-footer">
              <button className="btn-secondary" onClick={() => setShowTeamModal(false)}>{t('userMgmt.common.cancel' as any)}</button>
              <button className="um-btn-primary" onClick={handleCreateTeam} disabled={!newTeamName.trim()}>
                {t('userMgmt.teamModal.createBtn' as any)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Organization Modal */}
      {showConvertModal && (
        <div className="modal-overlay" onClick={() => setShowConvertModal(false)}>
          <div className="um-modal um-modal-convert" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h2><FontAwesomeIcon icon={faBuilding} /> {t('userMgmt.convertModal.title' as any)}</h2>
              <button className="close-btn" onClick={() => setShowConvertModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="um-modal-body">
              <div className="um-convert-hero">
                <div className="um-convert-icon">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <h3>{t('userMgmt.convertModal.heroTitle' as any)}</h3>
                <p>{t('userMgmt.convertModal.heroDesc' as any)}</p>
              </div>
              <div className="um-convert-features">
                <div className="um-convert-feature">
                  <FontAwesomeIcon icon={faUserPlus} />
                  <div>
                    <strong>{t('userMgmt.convertModal.featureInviteTitle' as any)}</strong>
                    <span>{t('userMgmt.convertModal.featureInviteDesc' as any)}</span>
                  </div>
                </div>
                <div className="um-convert-feature">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <div>
                    <strong>{t('userMgmt.convertModal.featureRbacTitle' as any)}</strong>
                    <span>{t('userMgmt.convertModal.featureRbacDesc' as any)}</span>
                  </div>
                </div>
                <div className="um-convert-feature">
                  <FontAwesomeIcon icon={faLayerGroup} />
                  <div>
                    <strong>{t('userMgmt.convertModal.featureTeamsTitle' as any)}</strong>
                    <span>{t('userMgmt.convertModal.featureTeamsDesc' as any)}</span>
                  </div>
                </div>
              </div>
              <div className="um-convert-note">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>{t('userMgmt.convertModal.note' as any)}</span>
              </div>
            </div>
            <div className="um-modal-footer">
              <button className="btn-secondary" onClick={() => setShowConvertModal(false)}>
                {t('userMgmt.common.cancel' as any)}
              </button>
              <button className="um-btn-primary" onClick={handleConvertToOrg} disabled={converting}>
                <FontAwesomeIcon icon={faBuilding} /> {converting ? t('userMgmt.convertModal.converting' as any) : t('userMgmt.convertModal.convertNow' as any)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
